import {
  ADD_PROJECT, EDIT_PROJECT, DELETE_PROJECT, SET_PROJECTS, ADD_SCHEDULE, SET_SCHEDULE, EDIT_SCHEDULE, DELETE_SCHEDULE, SET_SCHEDULE_ACTIVITIES, ADD_ACTIVITY,
  SET_ACTIVITY, EDIT_ACTIVITY, DELETE_ACTIVITY, SET_ACTIVITIES, LOGOUT, SET_PROJECT_SCHEDULES, ADD_USER, EDIT_USER, DELETE_USER, PUBLISH_SCHEDULE, UNPUBLISH_SCHEDULE,
  SET_PROJECT_CONTRACTORS, GET_PROJECT_CONTRACTORS, ADD_DAILY_REPORT, GET_DAILY_REPORT, GET_DAILY_REPORT_SUMMARIES, DELETE_DAILY_REPORT, GET_SCHEDULE_MESSAGE_THREADS,
  ADD_SCHEDULE_MESSAGE_THREAD, ADD_SCHEDULE_MESSAGE, GET_SCHEDULE_MESSAGES, ADD_UNREAD_SCHEDULE_MESSAGE_THREAD, ADD_ALL_UNREAD_SCHEDULE_MESSAGE_THREADS,
  ADD_ALL_SUBSCRIPTIONS, ADD_PROJECT_MEMBER, REMOVE_PROJECT_MEMBER, CHANGE_PROJECT_MEMBER_ROLE, CREATE_TRIAL_SUBSCRIPTION, SET_NAME, GET_SCHEDULE,
  CANCEL_PENDING_STANDARD_SUBSCRIPTION, SET_STARTUP_WELCOME_ENABLED, ADD_SUBSCRIPTION_ADMIN, GET_SUBSCRIPTION_INVITES, ENTER_SCHEDULE_MESSAGE_THREAD,
  LEAVE_SCHEDULE_MESSAGE_THREAD, WEBSOCKET_AUTHORIZED, REJECT_SUBSCRIPTION_INVITE, ACCEPT_SUBSCRIPTION_INVITE, REMOVE_SUBSCRIPTION_ADMIN,
  REMOVE_PENDING_SUBSCRIPTION_ADMIN, CHANGE_PENDING_PROJECT_MEMBER_ROLE, REMOVE_PENDING_PROJECT_MEMBER, userCanViewProject, SET_DARK_MODE, GET_PROJECT_SCHEDULE_CONTACTS,
  ADD_PROJECT_SCHEDULE_CONTACTS, DELETE_PROJECT_SCHEDULE_CONTACT, ADD_TEMPORARY_ACTIVITY, REPLACE_TEMPORARY_ACTIVITY_ID,
  SETUP_LOCAL_SESSION, GET_CONTACT_SHARED_SCHEDULE_STATS, PROMO_CODE_CREATE, PROMO_CODE_FETCH_ALL, PROMO_CODE_SET_NOTE, PROMO_CODE_SET_EXPIRATION, PROMO_CODE_DELETE,
  PROMO_CODE_REDEEM, PROMO_CODE_SET_DISABLED,
} from '../actions/types';
import { isArray, isObject, objectValuesToList } from '../Util';

const initialState = {
  userId: '',
  users: {}, 
  projects: {}, 
  schedules: {}, 
  activities: {}, 
  contractorList: {},
  dailyReports: {},
  dailyReportSummaries: {},
  scheduleMessageThreads: {},
  scheduleMessages: {},
  unreadScheduleMessageThreads: {},
  subscriptions: {},
  subscriptionInvites: {},
  userPreferences: {},
  temporaryActivityIds: {},
  subscriptionContacts: {},
  projectsContactEmails: {},
  localOnlyMode: false,
  localEditScheduleId: '',
  localEditScheduleProjectId: '',
  contactSharedScheduleStats: {}, // map of scheduleIds to map of contact emails
  promoCodes: {}, // map of PromoCode.id: PromoCode
};

// This is used to process the output from sending an action to the server, so the payload will correspond to the output of the api action type in types.mjs

const reducer = (state = initialState, action) => {
  const currentTimestamp = new Date().getTime();

  if(action.type === LOGOUT) {
    return initialState;
  }

  if(action.type === WEBSOCKET_AUTHORIZED) {
    return {...state, userId: action.payload.userId};
  }

  if(action.type === SET_PROJECTS) {
    if(!isArray(action.payload)) return state;

    const newProjects = {};
    for(const project of action.payload) {
      newProjects[project.id] = project;
    }
    return {...state, projects: newProjects};
  }

  if(action.type === ADD_USER || action.type === EDIT_USER) {
    return {
      ...state,
      users: {...state.users, [action.payload.id]: action.payload},
    };
  }

  if(action.type === DELETE_USER) {
    const updatedUsers = {...state.users};
    delete updatedUsers[action.payload.id];

    return {
      ...state,
      users: updatedUsers,
    };
  }

  if(action.type === SET_NAME) {
    return {
      ...state,
      users: {...state.users, [action.payload.user.id]: action.payload.user},
    };
  }

  if(action.type === ADD_PROJECT) {
    return {
      ...state, 
      projects: {...state.projects, [action.payload.project.id]: action.payload.project},
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
      users: {...state.users, [action.payload.user.id]: action.payload.user},
    };
  }

  if(action.type === EDIT_PROJECT) {
    const newProjects = {...state.projects};
    newProjects[action.payload.project.id] = action.payload.project;
    return {...state, projects: newProjects};
  }

  if(action.type === DELETE_PROJECT) {
    const newProjects = {...state.projects};
    delete newProjects[action.payload.projectId];

    const newUsers = {...state.users};
    for(const updatedUser of action.payload.users) {
      newUsers[updatedUser.id] = updatedUser;
    }

    return {
      ...state, 
      projects: newProjects, 
      users: newUsers,
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
    };
  }

  if(action.type === SET_PROJECT_SCHEDULES) {
    if(!isObject(action.payload)) return state;
    if(!isArray(action.payload.schedules)) return state;
    if(!action.payload.projectId) return state;

    // filter out schedules for this projectId
    const newSchedules = {};
    for(const key in state.schedules) {
      if(state.schedules[key].projectId !== action.payload.projectId) {
        newSchedules[key] = state.schedules[key];
      }
    }

    for(const schedule of action.payload.schedules) {
      newSchedules[schedule.id] = schedule;
    }

    return {...state, schedules: newSchedules};
  }

  if(action.type === SET_SCHEDULE_ACTIVITIES) {
    if(!isObject(action.payload)) return state;
    if(!isArray(action.payload.activities)) return state;
    if(!action.payload.scheduleId) return state;

    // filter out activities for this scheduleId
    const newActivities = {};
    for(const key in state.activities) {
      if(state.activities[key].scheduleId !== action.payload.scheduleId) {
        newActivities[key] = state.activities[key];
      }
    }

    for(const activity of action.payload.activities) {
      newActivities[activity.id] = activity;
    }

    return {...state, activities: newActivities};
  }

  if(action.type === ADD_SCHEDULE) {
    const newActivities = {};
    for(const activity of action.payload.activities) {
      newActivities[activity.id] = activity;
    }

    return {
      ...state, 
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
      projects: {...state.projects, [action.payload.project.id]: action.payload.project},
      schedules: {...state.schedules, [action.payload.schedule.id]: action.payload.schedule}, 
      activities: {...state.activities, ...newActivities},
    };
  }

  if(action.type === EDIT_SCHEDULE || action.type === PUBLISH_SCHEDULE || action.type === UNPUBLISH_SCHEDULE) {
    return {
      ...state, 
      schedules: {...state.schedules, [action.payload.schedule.id]: action.payload.schedule}, 
    };
  }

  if(action.type === SET_SCHEDULE) {
    const newSchedules = {...state.schedules};
    newSchedules[action.payload.id] = action.payload;
    return {...state, schedules: newSchedules};
  }

  if(action.type === DELETE_SCHEDULE) {
    const newSchedules = {...state.schedules};
    delete newSchedules[action.payload.scheduleId];
    return {
      ...state, 
      projects: {...state.projects, [action.payload.project.id]: action.payload.project},
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
      schedules: newSchedules,
    };
  }
  
  if(action.type === SET_ACTIVITIES) {
    const newActivities = {};
    for(const activity of action.payload) {
      newActivities[activity.id] = activity;
    }

    return {...state, activities: newActivities};
  }

  if(action.type === ADD_ACTIVITY || action.type === EDIT_ACTIVITY) {
    return {
      ...state, 
      activities: {...state.activities, [action.payload.activity.id]: action.payload.activity},
    };
  }

  if(action.type === ADD_TEMPORARY_ACTIVITY) {
    return {
      ...state, 
      activities: {...state.activities, [action.payload.activity.id]: action.payload.activity},
      temporaryActivityIds: {...state.temporaryActivityIds, [action.payload.activity.id]: ''},
    };
  }

  if(action.type === REPLACE_TEMPORARY_ACTIVITY_ID) {
    if(!(action.payload.temporaryActivityId in state.activities)) return state;

    const newActivities = {...state.activities};
    newActivities[action.payload.serverGeneratedId] = newActivities[action.payload.temporaryActivityId];
    newActivities[action.payload.serverGeneratedId].id = action.payload.serverGeneratedId;
    delete newActivities[action.payload.temporaryActivityId];

    const newTemporaryActivityIds = {...state.temporaryActivityIds};
    delete newTemporaryActivityIds[action.payload.temporaryActivityId];

    return {...state, activities: newActivities, temporaryActivityIds: newTemporaryActivityIds};
  }

  if(action.type === SET_ACTIVITY) {
    const newActivities = {...state.activities};
    newActivities[action.payload.id] = action.payload;
    return {...state, activities: newActivities};
  }

  if(action.type === DELETE_ACTIVITY) {
    const newActivities = {...state.activities};
    delete newActivities[action.payload.activityId];
    const newTemporaryActivityIds = {...state.temporaryActivityIds};
    delete newTemporaryActivityIds[action.payload.activityId];
    return {...state, activities: newActivities, temporaryActivityIds: newTemporaryActivityIds};
  }

  if(action.type === SET_PROJECT_CONTRACTORS) {
    return {
      ...state, 
      contractorList: {...state.contractorList, [action.payload.contractorList.projectId]: action.payload.contractorList},
    };
  }

  if(action.type === GET_PROJECT_CONTRACTORS) {
    return {
      ...state, 
      contractorList: {...state.contractorList, [action.payload.contractorList.projectId]: action.payload.contractorList},
    };
  }

  if(action.type === ADD_DAILY_REPORT) {
    return { 
      ...state, 
      dailyReports: {...state.dailyReports, [action.payload.dailyReport.id]: action.payload.dailyReport}, 
    };
  }

  if(action.type === GET_DAILY_REPORT) {
    return { 
      ...state, 
      dailyReports: {...state.dailyReports, [action.payload.dailyReport.id]: action.payload.dailyReport}, 
    };
  }

  if(action.type === DELETE_DAILY_REPORT) {
    const newDailyReports = {...state.dailyReports};
    delete newDailyReports[action.payload.dailyReportId];
    const newDailyReportSummaries = {...state.dailyReportSummaries};
    delete newDailyReportSummaries[action.payload.dailyReportId];
    return {
      ...state, 
      dailyReports: newDailyReports,
      dailyReportSummaries: newDailyReportSummaries,
    };
  }

  if(action.type === GET_DAILY_REPORT_SUMMARIES) {
    const newDailyReportSummaries = {...state.dailyReportSummaries};
    for(const reportSummary of action.payload.dailyReportSummaries) {
      newDailyReportSummaries[reportSummary.id] = reportSummary;
    }
    return { ...state, dailyReportSummaries: newDailyReportSummaries };
  }

  if(action.type === ADD_SCHEDULE_MESSAGE_THREAD) {
    return { 
      ...state, 
      scheduleMessageThreads: {...state.scheduleMessageThreads, [action.payload.scheduleMessageThread.id]: action.payload.scheduleMessageThread},
    };
  }
  if(action.type === GET_SCHEDULE_MESSAGE_THREADS) {
    const newScheduleMessageThreads = {...state.scheduleMessageThreads};
    for(const scheduleMessageThread of action.payload.scheduleMessageThreads) {
      newScheduleMessageThreads[scheduleMessageThread.id] = scheduleMessageThread;
    }
    const newScheduleMessages = {...state.scheduleMessages};
    for(const scheduleMessage of action.payload.scheduleMessages) {
      newScheduleMessages[scheduleMessage.id] = scheduleMessage;
    }
    return { ...state, scheduleMessageThreads: newScheduleMessageThreads, scheduleMessages: newScheduleMessages };
  }

  if(action.type === ADD_SCHEDULE_MESSAGE) {
    return { 
      ...state, 
      scheduleMessages: {...state.scheduleMessages, [action.payload.scheduleMessage.id]: action.payload.scheduleMessage}, 
      scheduleMessageThreads: {...state.scheduleMessageThreads, [action.payload.scheduleMessageThread.id]: action.payload.scheduleMessageThread}, 
    };
  }
  if(action.type === GET_SCHEDULE_MESSAGES) {
    const newScheduleMessages = {...state.scheduleMessages};
    for(const scheduleMessage of action.payload.scheduleMessages) {
      newScheduleMessages[scheduleMessage.id] = scheduleMessage;
    }
    return { ...state, scheduleMessages: newScheduleMessages };
  }

  if(action.type === ADD_UNREAD_SCHEDULE_MESSAGE_THREAD) {
    const newUnreadScheduleMessageThreads = {...state.unreadScheduleMessageThreads};
    newUnreadScheduleMessageThreads[action.payload.unreadScheduleMessageThread.scheduleMessageThreadId] = action.payload.unreadScheduleMessageThread;
    return { ...state, unreadScheduleMessageThreads: newUnreadScheduleMessageThreads };
  }
  if(action.type === ADD_ALL_UNREAD_SCHEDULE_MESSAGE_THREADS) {
    const newUnreadScheduleMessageThreads = {...state.unreadScheduleMessageThreads};
    for(const unreadScheduleMessageThread of action.payload.unreadScheduleMessageThreads) {
      newUnreadScheduleMessageThreads[unreadScheduleMessageThread.scheduleMessageThreadId] = unreadScheduleMessageThread;
    }
    return { ...state, unreadScheduleMessageThreads: newUnreadScheduleMessageThreads };
  }

  if(action.type === ADD_ALL_SUBSCRIPTIONS) {
    const newSubscriptions = {...state.subscriptions};
    for(const subscription of action.payload.subscriptions) {
      newSubscriptions[subscription.id] = subscription;
    }
    return { ...state, subscriptions: newSubscriptions };
  }

  if(action.type === ADD_PROJECT_MEMBER) {
    return {
      ...state,
      projects: {...state.projects, [action.payload.project.id]: action.payload.project},
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
      users: {...state.users, [action.payload.user.id]: action.payload.user},
    };
  }

  if(action.type === REMOVE_PROJECT_MEMBER) {
    const newProjects = {...state.projects, [action.payload.project.id]: action.payload.project};
    if(!userCanViewProject(state.userId, action.payload.subscription, action.payload.project)) {
      delete newProjects[action.payload.project.id]; // delete project if we can no longer view it
    }
    return {
      ...state,
      projects: newProjects,
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
      users: {...state.users, [action.payload.user.id]: action.payload.user},
    };
  }

  if(action.type === REMOVE_PENDING_PROJECT_MEMBER) {
    return {
      ...state,
      projects: {...state.projects, [action.payload.project.id]: action.payload.project},
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
    };
  }

  if(action.type === CHANGE_PROJECT_MEMBER_ROLE) {
    return {
      ...state,
      projects: {...state.projects, [action.payload.project.id]: action.payload.project},
    };
  }

  if(action.type === CHANGE_PENDING_PROJECT_MEMBER_ROLE) {
    return {
      ...state,
      projects: {...state.projects, [action.payload.project.id]: action.payload.project},
    };
  }

  if(action.type === CREATE_TRIAL_SUBSCRIPTION) {
    return {
      ...state, 
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
      users: {...state.users, [action.payload.user.id]: action.payload.user},
      userPreferences: action.payload.userPreferences,
    };
  }

  if(action.type === GET_SCHEDULE) {
    const newActivities = {...state.activities};
    for(const activity of action.payload.activities) {
      newActivities[activity.id] = activity;
    }

    return {
      ...state, 
      schedules: {...state.schedules, [action.payload.schedule.id]: action.payload.schedule}, 
      activities: newActivities,
    };
  }

  if(action.type === CANCEL_PENDING_STANDARD_SUBSCRIPTION) {
    return {
      ...state, 
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
    };
  }

  if(action.type === SET_STARTUP_WELCOME_ENABLED) {
    return {
      ...state, 
      userPreferences: action.payload.userPreferences,
    };
  }

  if(action.type === SET_DARK_MODE) {
    return { 
      ...state, 
      userPreferences: action.payload.userPreferences,
    };
  }

  if(action.type === ADD_SUBSCRIPTION_ADMIN) {
    const newProjects = {...state.projects};
    for(const project of action.payload.projects) {
      newProjects[project.id] = project;
    }

    return {
      ...state, 
      projects: newProjects,
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
      //users: {...state.users, [action.payload.user.id]: action.payload.user},
    };
  }

  if(action.type === REMOVE_SUBSCRIPTION_ADMIN) {
    const newSubscriptions = {...state.subscriptions};
    newSubscriptions[action.payload.subscription.id] = action.payload.subscription;
    if(state.userId === action.payload.user.id && !(action.payload.user.id in action.payload.subscription.members)) {
      delete newSubscriptions[action.payload.subscription.id]; // remove subscription if we're no longer a member
    }

    // remove projects that might have been only visible because client was an account manager
    const newProjects = {...state.projects};
    for(const project of objectValuesToList(newProjects)) {
      if(!(project.subscriptionId in newSubscriptions) || !userCanViewProject(state.userId, newSubscriptions[project.subscriptionId], project)) {
        delete newProjects[project.id];
      }
    }

    return {
      ...state, 
      subscriptions: newSubscriptions,
      projects: newProjects,
      users: {...state.users, [action.payload.user.id]: action.payload.user},
    };
  }

  if(action.type === REMOVE_PENDING_SUBSCRIPTION_ADMIN) {
    return {
      ...state, 
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
    };
  }

  if(action.type === GET_SUBSCRIPTION_INVITES) {
    const newSubscriptionInvites = {...state.subscriptionInvites};
    for(const subscriptionInvite of action.payload.subscriptionInvites) {
      newSubscriptionInvites[subscriptionInvite.subscriptionId] = subscriptionInvite;
    }

    return {
      ...state, 
      subscriptionInvites: newSubscriptionInvites,
    };
  }

  if(action.type === REJECT_SUBSCRIPTION_INVITE) {
    const newSubscriptionInvites = {...state.subscriptionInvites};
    delete newSubscriptionInvites[action.payload.subscription.id];

    const newProjects = {...state.projects};
    for(const project of action.payload.projects) {
      newProjects[project.id] = project;
    }

    return {
      ...state, 
      projects: newProjects,
      subscriptionInvites: newSubscriptionInvites,
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
    };
  }

  if(action.type === ACCEPT_SUBSCRIPTION_INVITE) {
    const newSubscriptionInvites = {...state.subscriptionInvites};
    delete newSubscriptionInvites[action.payload.subscription.id];

    const newUsers = {...state.users};
    for(const updatedUser of action.payload.users) {
      newUsers[updatedUser.id] = updatedUser;
    }

    const newProjects = {...state.projects};
    for(const project of action.payload.projects) {
      newProjects[project.id] = project;
    }

    return {
      ...state, 
      projects: newProjects,
      users: newUsers,
      subscriptionInvites: newSubscriptionInvites,
      subscriptions: {...state.subscriptions, [action.payload.subscription.id]: action.payload.subscription},
    };
  }

  if(action.type === ENTER_SCHEDULE_MESSAGE_THREAD) {
    // remove unread schedule message thread for the userId that just entered the thread
    const newUnreadScheduleMessageThreads = {};
    for(const unreadThread of objectValuesToList(state.unreadScheduleMessageThreads)) {
      if(unreadThread.scheduleMessageThreadId === action.payload.scheduleMessageThread.id && action.payload.userId === state.userId) continue;
      newUnreadScheduleMessageThreads[unreadThread.scheduleMessageThreadId] = unreadThread;
    }

    return { 
      ...state, 
      unreadScheduleMessageThreads: newUnreadScheduleMessageThreads,
      scheduleMessageThreads: {...state.scheduleMessageThreads, [action.payload.scheduleMessageThread.id]: action.payload.scheduleMessageThread},
    };
  }

  if(action.type === LEAVE_SCHEDULE_MESSAGE_THREAD) {
    return { 
      ...state, 
      scheduleMessageThreads: {...state.scheduleMessageThreads, [action.payload.scheduleMessageThread.id]: action.payload.scheduleMessageThread},
    };
  }

  if(action.type === GET_PROJECT_SCHEDULE_CONTACTS || action.type === ADD_PROJECT_SCHEDULE_CONTACTS) {
    const newContactEmailsForProject = {...(state.projectsContactEmails[action.payload.projectId] || {})};
    const newContactsForSubscription = {...(state.subscriptionContacts[action.payload.subscriptionId] || {})};

    for(const contact of action.payload.contacts) {
      newContactEmailsForProject[contact.email] = '';
      newContactsForSubscription[contact.email] = contact;
    }

    return {
      ...state, 
      projectsContactEmails: {...state.projectsContactEmails, [action.payload.projectId]: newContactEmailsForProject},
      subscriptionContacts: {...state.subscriptionContacts, [action.payload.subscriptionId]: newContactsForSubscription},
      projects: {...state.projects, [action.payload.projectId]: {...state.projects[action.payload.projectId], contactCount: action.payload.projectContactCount}},
      subscriptions: {...state.subscriptions, [action.payload.subscriptionId]: {...state.subscriptions[action.payload.subscriptionId], contactCount: action.payload.subscriptionContactCount}},
    };
  }
  if(action.type === DELETE_PROJECT_SCHEDULE_CONTACT) {
    const newContactEmailsForProject = {...(state.projectsContactEmails[action.payload.projectId] || {})};
    delete newContactEmailsForProject[action.payload.contactEmail];

    // remove contact from state.subscriptionContacts if it isn't in any other projects
    const newContactsForSubscription = {...(state.subscriptionContacts[action.payload.subscriptionId] || {})};
    let contactInOtherProjects = false;
    for(const projectId in state.projectsContactEmails) {
      if(projectId === action.payload.projectId) continue;
      if(!(action.payload.contactEmail in state.projectsContactEmails[projectId])) continue;
      contactInOtherProjects = true;
      break;
    }
    if(!contactInOtherProjects) {
      delete newContactsForSubscription[action.payload.contactEmail];
    }

    return {
      ...state, 
      projectsContactEmails: {...state.projectsContactEmails, [action.payload.projectId]: newContactEmailsForProject},
      subscriptionContacts: {...state.subscriptionContacts, [action.payload.subscriptionId]: newContactsForSubscription},
      projects: {...state.projects, [action.payload.projectId]: {...state.projects[action.payload.projectId], contactCount: action.payload.projectContactCount}},
      subscriptions: {...state.subscriptions, [action.payload.subscriptionId]: {...state.subscriptions[action.payload.subscriptionId], contactCount: action.payload.subscriptionContactCount}},
    };
  }
  if(action.type === GET_CONTACT_SHARED_SCHEDULE_STATS) {
    const newContactSharedScheduleStats = {...(state.contactSharedScheduleStats || {})};

    for(const contactSharedScheduleStats of action.payload.contactSharedScheduleStats) {
      if(!(contactSharedScheduleStats.scheduleId in newContactSharedScheduleStats)) {
        newContactSharedScheduleStats[contactSharedScheduleStats.scheduleId] = {};
      }
      newContactSharedScheduleStats[contactSharedScheduleStats.scheduleId][contactSharedScheduleStats.email] = contactSharedScheduleStats;
    }

    return {
      ...state, 
      contactSharedScheduleStats: newContactSharedScheduleStats,
    };
  }

  if(action.type === SETUP_LOCAL_SESSION) {
    if(state.localOnlyMode === true) return state;

    const newUsers = {};
    for(const user of action.payload.users) {
      newUsers[user.id] = user;
    }

    const newProjects = {};
    for(const project of action.payload.projects) {
      newProjects[project.id] = project;
    }

    const newSchedules = {};
    for(const schedule of action.payload.schedules) {
      newSchedules[schedule.id] = schedule;
    }

    const newActivities = {};
    for(const activity of action.payload.activities) {
      newActivities[activity.id] = activity;
    }

    const newSubscriptions = {};
    for(const subscription of action.payload.subscriptions) {
      newSubscriptions[subscription.id] = subscription;
    }

    return {
      ...state,
      userId: action.payload.userId,
      users: newUsers,
      activities: newActivities,
      projects: newProjects,
      schedules: newSchedules,
      subscriptions: newSubscriptions,
      userPreferences: action.payload.userPreferences,
      localOnlyMode: true,
      localEditScheduleId: action.payload.schedules[0].id,
      localEditScheduleProjectId: action.payload.projects[0].id,
    };
  }

  if(action.type === PROMO_CODE_CREATE || action.type === PROMO_CODE_SET_EXPIRATION || action.type === PROMO_CODE_SET_NOTE || action.type === PROMO_CODE_REDEEM || action.type === PROMO_CODE_SET_DISABLED) {
    return {
      ...state,
      promoCodes: {...state.promoCodes, [action.payload.promoCode.id]: action.payload.promoCode},
    };
  }
  if(action.type === PROMO_CODE_FETCH_ALL) {
    const newPromoCodes = {...state.promoCodes};
    for(const promoCode of action.payload.promoCodes) {
      newPromoCodes[promoCode.id] = promoCode;
    }

    return {
      ...state,
      promoCodes: newPromoCodes,
    };
  }
  if(action.type === PROMO_CODE_DELETE) {
    const newPromoCodes = {...state.promoCodes};
    delete newPromoCodes[action.payload.promoCodeId];

    return {
      ...state,
      promoCodes: newPromoCodes,
    };
  }

  return state; // default
};

export default reducer;
